import _ from 'lodash';
import {
  AUTH_ATTACH_USER_ID,
  AUTH_FINISH_ONBOARDING,
  AUTH_LOGIN,
  AUTH_LOGOUT,
  AUTH_SESSION_EXPIRED,
  AUTH_UPDATE_USER,
  AUTH_UPDATE_TOKEN,
  AUTH_SET_REGION,
  PROFILE_COMPLETED,
  AUTH_UPDATE_TOKEN_DETAILS,
  AUTH_UPDATE_AUTH_CONTEXT,
  AUTH_ACTIVATE_ACCOUNT,
  AUTH_CLEAR_SIGN_OUT_CODE,
  AUTH_SET_START_WITH_BIOMETRIC,
  AUTH_SET_SUGGEST_BIOMETRIC_ENROLLMENT,
  AUTH_SET_PENDING_BIOMETRIC_ENROLLMENT,
  AUTH_SET_PENDING_DEEP_LINK,
  BIOMETRIC_ENROLLMENT_CLOSED,
  USERS_MGMT_UPDATE_USER,
} from '.';
import authenticationServiceApi from '../../api/authentication.service.api';
import userServiceApi from '../../api/user.service.api';
import { queryKeys } from '../../api/query-client-registry';
import { invalidateQueryCache } from './query-cache.actions';
import { USER_PROFILE_SOURCE } from '../../constants';
import { SIGN_OUT_CODES } from '../../constants/auth.constants';
import { GENERIC_SERVER_ERROR_MESSAGE } from '../../constants/applicationErrorCodes';

export const signIn =
  ({ accessToken, refreshToken, user }) =>
  async dispatch => {
    dispatch({
      type: AUTH_LOGIN,
      payload: { accessToken, refreshToken, user },
    });
  };

export const activateAccount = () => async dispatch =>
  dispatch({
    type: AUTH_ACTIVATE_ACCOUNT,
  });

export const updateToken =
  ({ accessToken, refreshToken }) =>
  dispatch => {
    dispatch(updateTokenDetails({ accessToken }));

    const payload = {
      accessToken,
      refreshToken,
    };
    const parsedPayload = _.omitBy(payload, _.isUndefined);

    dispatch({
      type: AUTH_UPDATE_TOKEN,
      payload: parsedPayload,
    });
  };

export const updateTokenDetails =
  ({ accessToken }) =>
  dispatch => {
    dispatch({
      type: AUTH_UPDATE_TOKEN_DETAILS,
      payload: { accessToken },
    });
  };

export const updateAuthenticationContext =
  ({ context }) =>
  dispatch => {
    dispatch({
      type: AUTH_UPDATE_AUTH_CONTEXT,
      payload: { context },
    });
  };

export const attachUserDetails =
  (user, { replace = false } = {}) =>
  dispatch => {
    dispatch({
      type: AUTH_UPDATE_USER,
      payload: { user, replace },
    });
  };

export const setRegion =
  ({ region }) =>
  dispatch => {
    dispatch({
      type: AUTH_SET_REGION,
      payload: { region },
    });
  };

export const attachUserId = ({ id }) => ({
  type: AUTH_ATTACH_USER_ID,
  payload: { id },
});

/**
 * @param {{ skipApiCall?: boolean, signOutCode?: string }} [options]
 */
export const logout =
  ({ skipApiCall = false, signOutCode = SIGN_OUT_CODES.USER_INITIATED } = {}) =>
  async dispatch => {
    if (!skipApiCall) {
      await authenticationServiceApi.logout();
    }

    dispatch({ type: AUTH_LOGOUT, payload: { signOutCode } });
  };

export const clearSignOutCode = () => ({
  type: AUTH_CLEAR_SIGN_OUT_CODE,
});

export const sessionExpired = ({ signOutCode = null } = {}) => ({
  type: AUTH_SESSION_EXPIRED,
  payload: { signOutCode },
});

export const setStartWithBiometric = value => dispatch => {
  dispatch({ type: AUTH_SET_START_WITH_BIOMETRIC, payload: value });
};

export const setSuggestBiometricEnrollment = value => dispatch => {
  dispatch({ type: AUTH_SET_SUGGEST_BIOMETRIC_ENROLLMENT, payload: value });
};

export const setPendingBiometricEnrollment = value => dispatch => {
  dispatch({ type: AUTH_SET_PENDING_BIOMETRIC_ENROLLMENT, payload: value });
};

export const setPendingDeepLink = value => dispatch => {
  dispatch({ type: AUTH_SET_PENDING_DEEP_LINK, payload: value });
};

// Signal only (no reducer): releases prompts held behind the enrollment screen.
export const biometricEnrollmentClosed = () => ({ type: BIOMETRIC_ENROLLMENT_CLOSED });

export const finishOnboarding = () => async dispatch => {
  dispatch({ type: AUTH_FINISH_ONBOARDING });
};

export const updateUserData =
  ({ user: userData, source = USER_PROFILE_SOURCE.PROFILE_PAGE }) =>
  async dispatch => {
    const res = await userServiceApi.updateUserProfile({ user: userData });

    if (res.error) {
      return res;
    }

    const updatedUser = res?.data?.user;

    if (!updatedUser) {
      return { error: GENERIC_SERVER_ERROR_MESSAGE };
    }

    dispatch({
      type: AUTH_UPDATE_USER,
      payload: {
        user: updatedUser,
      },
    });

    // Keep the user-management list in sync so it shows the latest values without a refetch.
    // No-ops in the reducer when the edited user isn't loaded in that list.
    if (updatedUser?.id) {
      dispatch({
        type: USERS_MGMT_UPDATE_USER,
        payload: { id: updatedUser.id, user: updatedUser },
      });
    }

    // Profile fields may have changed — refresh the org-structure usage counts.
    dispatch(invalidateQueryCache(queryKeys.userGroupStats));

    if (
      source === USER_PROFILE_SOURCE.POPUP &&
      !userData?.profileCompletedAt &&
      res?.data?.user?.profileCompletedAt
    ) {
      dispatch({
        type: PROFILE_COMPLETED,
      });
    }

    return res;
  };

export const snoozeProfile =
  ({ id }) =>
  async () => {
    const res = await userServiceApi.snoozeProfile({ id });

    if (res.error) {
      throw new Error(res.data.message);
    }
  };

export const fetchAuthenticatedUser = () => async dispatch => {
  const res = await userServiceApi.me();

  if (res.error) {
    throw new Error(res.data.message);
  }

  dispatch(attachUserDetails(res.data.user, { replace: true }));
};
